import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * MobileBottomNav — Fixed bottom navigation bar for mobile only.
 * Profile tap opens a sheet with profile/deposit/withdraw/logout options.
 */
const MobileBottomNav = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('reddyannabook_user');
    localStorage.removeItem('reddyannabook_token');
    setProfileOpen(false);
    window.location.href = '/login';
  };

  const tabs = [
    { label: 'Home', icon: '🏠', path: '/' },
    { label: 'Cricket', icon: '🏏', path: '/cricket' },
    { label: 'IPL', icon: '🏆', path: '/ipl-2026' },
    { label: 'Casino', icon: '🎲', path: '/casino' },
    { label: 'Wallet', icon: '💰', path: '/wallet' },
    { label: 'Profile', icon: '👤', path: '__profile__' },
  ];

  const handleTab = (tab) => {
    if (tab.path === '__profile__') {
      if (!user) { navigate('/login'); return; }
      setProfileOpen(true);
    } else {
      navigate(tab.path);
    }
  };

  const profileItems = [
    { label: 'My Profile', icon: '👤', path: '/profile' },
    { label: 'Deposit', icon: '💰', path: '/deposit' },
    { label: 'Withdraw', icon: '📤', path: '/withdraw' },
    { label: 'Open Bets', icon: '📋', path: '/open-bets' },
    { label: 'Bet History', icon: '📄', path: '/bet-history' },
    { label: 'Profit & Loss', icon: '📊', path: '/win-loss' },
    { label: 'Account Statement', icon: '📋', path: '/account-statement' },
    { label: 'Refer & Earn', icon: '🎁', path: '/referral' },
    { label: 'Settings', icon: '⚙️', path: '/settings' },
  ];

  return (
    <>
      {/* Bottom Nav Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.1)]">
        <div className="flex items-center justify-around h-14">
          {tabs.map(tab => {
            const isActive = tab.path === '__profile__'
              ? profileOpen || location.pathname === '/profile'
              : location.pathname === tab.path;
            return (
              <button
                key={tab.label}
                onClick={() => handleTab(tab)}
                className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                  isActive ? 'text-lotus' : 'text-gray-500'
                }`}
              >
                <span className="text-lg leading-none">{tab.icon}</span>
                <span className={`text-[10px] mt-0.5 font-medium ${isActive ? 'text-lotus font-bold' : ''}`}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Profile Bottom Sheet */}
      {profileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40" onClick={() => setProfileOpen(false)} />
          {/* Sheet */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl animate-slide-up">
            {/* Handle + Header */}
            <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-lotus flex items-center justify-center text-white text-sm font-bold">
                  {(user?.display_name || user?.username || 'U')[0].toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900">{user?.display_name || user?.username || 'User'}</div>
                  <div className="text-[10px] text-gray-500">{user?.phone_number || ''}</div>
                </div>
              </div>
              <button onClick={() => setProfileOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
                <span className="text-gray-400 text-lg">✕</span>
              </button>
            </div>

            {/* Menu Items */}
            <div className="py-2">
              {profileItems.map(item => (
                <button key={item.path}
                  onClick={() => { setProfileOpen(false); navigate(item.path); }}
                  className="w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-50 active:bg-gray-100 transition-colors">
                  <span className="text-lg">{item.icon}</span>
                  <span className="text-sm font-medium text-gray-800">{item.label}</span>
                </button>
              ))}
            </div>

            {/* Logout */}
            <div className="border-t border-gray-100 py-2 mb-2">
              <button onClick={handleLogout}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-red-50 active:bg-red-100 transition-colors">
                <span className="text-lg">🚪</span>
                <span className="text-sm font-bold text-red-600">Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.25s ease-out; }
      `}</style>
    </>
  );
};

export default MobileBottomNav;
